/**
 * Pure helpers for the student dashboard (derive stats and section data from API payload).
 */

export function fullNameFromStudent(student) {
  if (!student) return '';
  return (
    student.fullName ||
    [student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ').trim() ||
    ''
  );
}

export function formatDisplayDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '—';
  }
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Module completion % for one enrollment (0–100). */
export function enrollmentModuleProgressPercent(enrollment) {
  const rows = enrollment?.moduleProgress;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const done = rows.filter((r) => r.isCompleted).length;
  return Math.round((done / rows.length) * 100);
}

export function courseStatusFromProgress(pct) {
  if (pct == null) return 'Not Started';
  if (pct >= 100) return 'Completed';
  if (pct > 0) return 'In Progress';
  return 'Not Started';
}

/**
 * @returns {{ enrolledCourses: number, assignmentsDue: number, avgScore: number | null, completionPct: number | null }}
 */
export function computeDashboardStats(student) {
  const enrollments = Array.isArray(student?.enrollments) ? student.enrollments : [];
  const courseIds = new Set();
  const batchIds = new Set();
  enrollments.forEach((e) => {
    if (e?.batchId != null) batchIds.add(String(e.batchId));
    const cid = e?.batch?.course?.id ?? e?.batch?.courseId;
    if (cid != null) courseIds.add(String(cid));
  });

  let sumPct = 0;
  let pctCount = 0;
  enrollments.forEach((e) => {
    const p = enrollmentModuleProgressPercent(e);
    if (p != null) {
      sumPct += p;
      pctCount += 1;
    }
  });

  const readiness = enrollments.map((e) => e?.interviewReadiness).filter(Boolean);
  const ratings = [];
  readiness.forEach((r) => {
    ['communicationSkillsRating', 'attitudeRating', 'presentationRating', 'technicalReadinessRating'].forEach(
      (k) => {
        const n = num(r[k]);
        if (n != null) ratings.push(n);
      }
    );
  });
  const avgScore =
    ratings.length > 0 ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10 : null;

  let assignmentsDue = 0;
  readiness.forEach((r) => {
    const d = r?.resumeSubmissionDeadline;
    if (d && !r?.resumeReadySubmitted) {
      const t = new Date(d).getTime();
      if (t >= Date.now()) assignmentsDue += 1;
    }
  });

  const enrolledCourses = courseIds.size || batchIds.size || enrollments.length;

  return {
    enrolledCourses,
    assignmentsDue,
    avgScore,
    completionPct: pctCount > 0 ? Math.round(sumPct / pctCount) : null
  };
}

/** Points for a simple trend: cumulative module completion over ordered completions. */
export function buildModuleCompletionTrend(enrollment) {
  const rows = Array.isArray(enrollment?.moduleProgress) ? [...enrollment.moduleProgress] : [];
  const dated = rows
    .filter((r) => r.isCompleted && r.completionDate)
    .sort((a, b) => new Date(a.completionDate) - new Date(b.completionDate));
  if (dated.length === 0) return [];
  const total = rows.length || dated.length;
  return dated.map((r, i) => ({
    label: formatDisplayDate(r.completionDate),
    value: Math.round(((i + 1) / total) * 100)
  }));
}

export function countCompletedModules(student) {
  let n = 0;
  const list = student?.enrollments;
  if (!Array.isArray(list)) return 0;
  list.forEach((en) => {
    (en.moduleProgress || []).forEach((m) => {
      if (m.isCompleted) n += 1;
    });
  });
  return n;
}

export function collectPlacementRows(enrollments) {
  const rows = [];
  const list = Array.isArray(enrollments) ? enrollments : [];
  list.forEach((en) => {
    const batchName = en?.batch?.batchName || '—';
    if (en?.placement?.company) {
      const p = en.placement;
      rows.push({
        key: `p-${en.id}`,
        company: p.company?.companyName || '—',
        interviewDate: p.placementDate || p.joiningDate,
        result: 'Placed',
        offerStatus: p.jobRole ? `Offer — ${p.jobRole}` : 'Offer confirmed',
        batchName
      });
    }
    const opps = en?.placementOpportunities || [];
    opps.forEach((o, idx) => {
      rows.push({
        key: `o-${en.id}-${o.id ?? idx}`,
        company: o.company?.companyName || '—',
        interviewDate: o.interviewDate || o.date,
        result: o.finalStatus || 'Pending',
        offerStatus: o.writtenTest != null ? `Written test: ${o.writtenTest ? 'Yes' : 'No'}` : '—',
        batchName
      });
    });
  });
  return rows;
}
